import React, { useEffect, useRef, useState } from "react";
import { Loader2, RotateCw, Check, X } from "lucide-react";
import { adminApi, resolveImage } from "../../lib/admin-api";

const LOADING_STEPS_NL = [
  "Onderzoeken van geselecteerde stukken…",
  "Prompt opstellen in hybride stijl…",
  "Renderen van fotorealistische helft…",
  "Renderen van potloodschets helft…",
  "Bijna klaar — beeld optimaliseren…",
];

/**
 * Shared modal for the AI room-image generation flow.
 *
 * Usage:
 *   <RoomImagePreviewModal
 *     open
 *     roomInput={{title_nl, title_en, intro_nl, intro_en}}
 *     classicIds={[uuid, uuid]}
 *     classicsLookup={[{id, name, designer, image_url}, ...]}  // for showing thumbs
 *     currentHeroUrl={form.hero_image_url}  // optional, shows "Huidige" thumbnail
 *     onSave={(previewId, previewUrl) => {...}}     // commit
 *     onClose={() => {...}}
 *   />
 *
 * The save callback is responsible for either calling
 * POST /api/admin/rooms/{slug}/save-generated-image (edit mode) or stashing the
 * preview_id into the create-room payload (create mode). The modal stays
 * agnostic — it only orchestrates the generate-regenerate-cancel loop.
 */
export default function RoomImagePreviewModal({
  open,
  roomInput,
  classicIds,
  classicsLookup,
  currentHeroUrl,
  onSave,
  onClose,
}) {
  const [status, setStatus] = useState("idle"); // idle | loading | result | saving | error
  const [stepIdx, setStepIdx] = useState(0);
  const [preview, setPreview] = useState(null); // {preview_id, preview_url, prompt_summary, classics_used}
  const [extraPrompt, setExtraPrompt] = useState("");
  const [error, setError] = useState("");
  const startedRef = useRef(false);
  const stepTimer = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (startedRef.current) return;
    startedRef.current = true;
    generate("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // rotate loading-step copy every ~3 seconds while generating
  useEffect(() => {
    if (status !== "loading") {
      if (stepTimer.current) { clearInterval(stepTimer.current); stepTimer.current = null; }
      return;
    }
    setStepIdx(0);
    stepTimer.current = setInterval(() => {
      setStepIdx((i) => Math.min(i + 1, LOADING_STEPS_NL.length - 1));
    }, 3500);
    return () => {
      if (stepTimer.current) clearInterval(stepTimer.current);
      stepTimer.current = null;
    };
  }, [status]);

  const generate = async (extra) => {
    setStatus("loading");
    setError("");
    setPreview(null);
    try {
      const r = await adminApi.post(
        "/rooms/generate-image",
        {
          title_nl: roomInput.title_nl || "",
          title_en: roomInput.title_en || "",
          intro_nl: roomInput.intro_nl || "",
          intro_en: roomInput.intro_en || "",
          classic_ids: classicIds,
          extra_prompt: extra || "",
        },
        { timeout: 180000 },
      );
      setPreview(r.data);
      setStatus("result");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Genereren mislukt");
      setStatus("error");
    }
  };

  const handleSave = async () => {
    if (!preview) return;
    setStatus("saving");
    try {
      await onSave(preview.preview_id, preview.preview_url);
      // parent closes the modal once committed
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Bewaren mislukt");
      setStatus("result");
    }
  };

  if (!open) return null;

  return (
    <div data-testid="room-image-preview-modal" className="fixed inset-0 z-[60] flex items-center justify-center bg-stoel-ink/80 backdrop-blur-sm">
      <div className="bg-stoel-paper max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stoel-line">
          <div>
            <div className="stoel-eyebrow text-stoel-walnut">— AI Ruimtebeeld</div>
            <h2 className="text-xl font-light text-stoel-ink">
              {roomInput.title_nl || roomInput.title_en || "Nieuwe ruimte"}
            </h2>
          </div>
          <button
            data-testid="room-image-modal-close"
            onClick={onClose}
            disabled={status === "loading" || status === "saving"}
            className="text-stoel-inkSoft hover:text-stoel-ink disabled:opacity-40"
            aria-label="Sluit"
          >
            <X />
          </button>
        </div>

        <div className="grid md:grid-cols-12 gap-0">
          {/* LEFT — image */}
          <div className="md:col-span-7 bg-stoel-paperDeep min-h-[55vh] flex items-center justify-center p-6">
            {status === "loading" || status === "saving" ? (
              <div data-testid="room-image-loading" className="flex flex-col items-center gap-5 text-center">
                <Loader2 className="animate-spin text-stoel-terracotta" size={42} />
                <div className="text-base text-stoel-ink">
                  {status === "saving" ? "Bewaren…" : LOADING_STEPS_NL[stepIdx]}
                </div>
                <div className="text-xs uppercase tracking-[0.25em] text-stoel-walnut">
                  {status === "saving" ? "" : "30–60 seconden"}
                </div>
              </div>
            ) : status === "result" && preview ? (
              <img
                data-testid="room-image-preview"
                src={resolveImage(preview.preview_url)}
                alt="AI-generated room preview"
                className="max-w-full max-h-[70vh] object-contain shadow-xl"
              />
            ) : status === "error" ? (
              <div className="text-center max-w-md">
                <div className="text-base text-stoel-terracotta mb-3">Genereren mislukt</div>
                <div className="text-sm text-stoel-inkSoft">{error}</div>
                <button
                  onClick={() => generate(extraPrompt)}
                  className="mt-6 px-4 py-2 border border-stoel-ink text-sm hover:bg-stoel-ink hover:text-stoel-paper"
                >
                  Opnieuw proberen
                </button>
              </div>
            ) : null}
          </div>

          {/* RIGHT — controls */}
          <div className="md:col-span-5 p-6 flex flex-col gap-5">
            {currentHeroUrl && (
              <div>
                <div className="stoel-eyebrow text-stoel-walnut mb-1.5">Huidig beeld</div>
                <img
                  src={resolveImage(currentHeroUrl)}
                  alt="Current room hero"
                  className="w-full h-24 object-cover bg-stoel-paperDeep border border-stoel-line"
                />
              </div>
            )}

            <div>
              <div className="stoel-eyebrow text-stoel-walnut mb-1.5">Gebaseerd op</div>
              <div className="flex flex-wrap gap-2">
                {classicsLookup.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center gap-2 border border-stoel-line bg-stoel-paperDeep/40 pr-2"
                  >
                    {c.image_url ? (
                      <img src={resolveImage(c.image_url)} alt="" className="w-9 h-9 object-cover" />
                    ) : (
                      <div className="w-9 h-9 bg-stoel-paperDeep" />
                    )}
                    <div>
                      <div className="text-xs text-stoel-ink line-clamp-1 max-w-[140px]">{c.name}</div>
                      <div className="text-[10px] text-stoel-walnut line-clamp-1 max-w-[140px]">{c.designer}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="stoel-eyebrow text-stoel-walnut mb-1.5 block">Wat moet anders? (optioneel)</label>
              <textarea
                data-testid="room-image-extra-prompt"
                value={extraPrompt}
                onChange={(e) => setExtraPrompt(e.target.value.slice(0, 300))}
                placeholder="bv. meer ochtendlicht, donkerder houten vloer, een kleed met geometrisch patroon…"
                rows={3}
                disabled={status === "loading" || status === "saving"}
                className="w-full bg-stoel-paper border border-stoel-line px-3 py-2 text-sm focus:outline-none focus:border-stoel-ink disabled:bg-stoel-paperDeep"
              />
              <div className="text-[10px] text-stoel-walnut text-right mt-1">{extraPrompt.length}/300</div>
            </div>

            {error && status === "result" && (
              <div className="bg-stoel-terracotta/10 border-l-2 border-stoel-terracotta p-3 text-sm">{error}</div>
            )}

            <div className="mt-auto flex flex-col gap-2">
              <button
                data-testid="room-image-regen"
                onClick={() => generate(extraPrompt)}
                disabled={status === "loading" || status === "saving"}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-stoel-ink text-sm hover:bg-stoel-ink hover:text-stoel-paper disabled:opacity-50"
              >
                <RotateCw size={14} /> Opnieuw genereren
              </button>
              <button
                data-testid="room-image-save"
                onClick={handleSave}
                disabled={status !== "result" || !preview}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-stoel-terracotta text-stoel-paper text-sm hover:bg-stoel-ink disabled:opacity-50"
              >
                <Check size={14} /> Bewaar als ruimte-beeld
              </button>
              <button
                data-testid="room-image-cancel"
                onClick={onClose}
                disabled={status === "loading" || status === "saving"}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-stoel-inkSoft hover:bg-stoel-paperDeep disabled:opacity-40"
              >
                Annuleer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
